import { CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { AuthCloseButton } from "@/components/AuthCloseButton";
import { Wordmark } from "@/components/brand/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t("auth.passwordMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail === "invalid_or_expired_token" ? t("auth.resetInvalidToken") : formatApiErrorDetail(detail) || t("auth.genericError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-20" data-testid="reset-password-page">
      <AuthCloseButton />
      <div className="w-full max-w-md flex flex-col gap-10">
        <div className="flex flex-col items-start gap-6">
          <Wordmark />
          <div>
            <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#0A0A0A]">{t("auth.resetTitle")}</h1>
            <p className="mt-2 text-sm text-[#737373]">{t("auth.resetSubtitle")}</p>
          </div>
        </div>

        {done ? (
          <div className="flex flex-col gap-6" data-testid="reset-password-done">
            <div className="rounded-2xl bg-[#1D4D3B]/[0.06] border border-[#1D4D3B]/15 px-6 py-5 flex items-start gap-4">
              <CheckCircle2 className="w-5 h-5 text-[#1D4D3B] mt-0.5 shrink-0" aria-hidden="true" />
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-[#0A0A0A]">{t("auth.resetDoneTitle")}</span>
                <span className="text-sm text-[#737373]">{t("auth.resetDoneDesc")}</span>
              </div>
            </div>
            <Link
              to="/signin"
              data-testid="reset-go-signin"
              className="inline-flex items-center justify-center h-12 rounded-full bg-[#0A0A0A] text-white text-base hover:bg-black/80 transition-colors duration-300"
            >
              {t("auth.signInBtn")}
            </Link>
          </div>
        ) : !token ? (
          <div className="flex flex-col gap-6" data-testid="reset-no-token">
            <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3" role="alert">
              {t("auth.resetInvalidToken")}
            </p>
            <Link to="/forgot-password" data-testid="reset-request-new" className="text-sm font-medium text-[#0A0A0A] underline underline-offset-4 w-fit">
              {t("auth.resetRequestNew")}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
            <div className="flex flex-col gap-2">
              <Label htmlFor="reset-password" className="text-sm font-medium">{t("auth.newPassword")}</Label>
              <Input
                id="reset-password"
                data-testid="reset-password-input"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-xl border-black/10 focus-visible:ring-1 focus-visible:ring-[#0A0A0A] bg-transparent"
              />
              <p className="text-xs text-[#737373]">{t("auth.passwordHint")}</p>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="reset-confirm" className="text-sm font-medium">{t("auth.confirmPassword")}</Label>
              <Input
                id="reset-confirm"
                data-testid="reset-confirm-input"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-12 rounded-xl border-black/10 focus-visible:ring-1 focus-visible:ring-[#0A0A0A] bg-transparent"
              />
            </div>
            {error && (
              <p data-testid="reset-error" role="alert" className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="reset-submit-button"
              disabled={submitting || !password || !confirm}
              className="h-12 rounded-full bg-[#0A0A0A] text-white hover:bg-black/80 text-base transition-colors duration-300 disabled:opacity-60"
            >
              {submitting ? `${t("common.loading")}…` : t("auth.resetBtn")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
